import { useEffect, useSyncExternalStore } from "react";
import { Logger } from "@cede/utils";

const WS_URL = "wss://websocket-perps.katana.network/v1";
const MAX_RECONNECT_ATTEMPTS = 10;
const MAX_RECONNECT_DELAY = 30_000;
const PING_INTERVAL = 15_000;

// --- Module-level singleton managing a single shared WebSocket connection ---

type SetupFn = (ws: WebSocket) => (() => void) | void;
type MessageListener = (data: unknown) => void;
type ErrorListener = (error: string) => void;
type StatusListener = () => void;

/**
 * A Katana WS subscription descriptor — the per-channel object that gets
 * wrapped in the `{ method, subscriptions: [...] }` envelope on the wire.
 * Examples:
 *   { l2orderbook: { markets: ["BTC-USDC-perp"] } }
 *   { candles:     { markets: ["BTC-USDC-perp"], interval: "1m" } }
 */
export type KatanaSubscription = Record<string, unknown>;

interface SubscriberEntry {
  setup?: SetupFn;
  onMessage?: MessageListener;
  onError?: ErrorListener;
  cleanup: (() => void) | null;
  /** Number of mounted hook instances sharing this subscriber */
  refCount: number;
  /** Deferred removal timeout — cancelled if a new instance re-mounts before it fires */
  pendingRemove?: NodeJS.Timeout;
}

interface SubscriptionEntry {
  subscription: KatanaSubscription;
  /** Number of active references to this wire subscription */
  refCount: number;
  /** Deferred unsubscribe timeout — cancelled if a new ref lands before it fires */
  pendingUnsubscribe?: NodeJS.Timeout;
}

let ws: WebSocket | null = null;
let isConnecting = false;
let isConnected = false;
let connectionError: string | null = null;
let reconnectAttempts = 0;
let reconnectTimeout: NodeJS.Timeout | undefined;
let disconnectTimeout: NodeJS.Timeout | undefined;
let pingInterval: NodeJS.Timeout | undefined;

/**
 * Subscriber registry keyed by caller-provided `id`.
 * Multiple hook instances with the same `id` share a single entry,
 * ref-counted across mounts so React Strict Mode double-runs don't churn.
 */
const subscribers = new Map<string, SubscriberEntry>();

/**
 * Reference-counted wire-subscription registry.
 * Key = stable JSON string of the subscription descriptor. The actual
 * subscribe message is sent only on the 0 → 1 transition; unsubscribe is
 * deferred so React Strict Mode cleanup → re-mount cycles collapse to a
 * no-op instead of churning the server-side subscription.
 */
const subscriptions = new Map<string, SubscriptionEntry>();
const statusListeners = new Set<StatusListener>();

let imperativeIdCounter = 0;

function subKey(sub: KatanaSubscription): string {
  return JSON.stringify(sub, Object.keys(sub).sort());
}

function notifyStatusListeners() {
  statusListeners.forEach((listener) => listener());
}

function sendWsSubscribe(sub: KatanaSubscription) {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ method: "subscribe", subscriptions: [sub] }));
  }
}

function sendWsUnsubscribe(sub: KatanaSubscription) {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ method: "unsubscribe", subscriptions: [sub] }));
  }
}

function startPing() {
  if (pingInterval) return;
  pingInterval = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ method: "ping" }));
    }
  }, PING_INTERVAL);
}

function stopPing() {
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = undefined;
  }
}

function runSetup(entry: SubscriberEntry) {
  if (!ws || ws.readyState !== WebSocket.OPEN || !entry.setup) return;
  entry.cleanup?.();
  const cleanup = entry.setup(ws);
  entry.cleanup = cleanup ?? null;
}

function runAllSetups() {
  subscribers.forEach((entry) => runSetup(entry));
}

function cleanupAllSubscribers() {
  subscribers.forEach((entry) => {
    entry.cleanup?.();
    entry.cleanup = null;
  });
}

function disconnect() {
  cleanupAllSubscribers();
  stopPing();

  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  // Clear any pending deferred unsubscribes — the connection is going away anyway.
  subscriptions.forEach((entry) => {
    if (entry.pendingUnsubscribe) {
      clearTimeout(entry.pendingUnsubscribe);
      entry.pendingUnsubscribe = undefined;
    }
  });

  if (ws) {
    ws.close();
    ws = null;
  }

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = false;
  isConnected = false;
  reconnectAttempts = 0;
  notifyStatusListeners();
}

function connect() {
  if (isConnecting || ws || subscribers.size === 0) return;

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = true;
  connectionError = null;
  notifyStatusListeners();

  try {
    const socket = new WebSocket(WS_URL);

    socket.onopen = () => {
      isConnected = true;
      isConnecting = false;
      reconnectAttempts = 0;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      notifyStatusListeners();
      startPing();

      // Re-subscribe all active wire subscriptions after a (re)connect
      subscriptions.forEach((entry) => {
        if (entry.refCount > 0) sendWsSubscribe(entry.subscription);
      });

      runAllSetups();
    };

    socket.onmessage = (event) => {
      if (typeof event.data !== "string") return;

      try {
        const msg = JSON.parse(event.data);
        subscribers.forEach((entry) => entry.onMessage?.(msg));
      } catch {
        // Ignore non-JSON frames (e.g. server-side heartbeat acks)
      }
    };

    socket.onerror = () => {
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      const errorMsg = "WebSocket connection error";
      connectionError = errorMsg;
      notifyStatusListeners();
      subscribers.forEach((entry) => entry.onError?.(errorMsg));
    };

    socket.onclose = () => {
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      ws = null;
      stopPing();
      cleanupAllSubscribers();
      notifyStatusListeners();

      if (subscribers.size > 0 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY);
        reconnectAttempts++;

        Logger.log(
          `[katanaWS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
          Logger.types.INFO,
        );

        reconnectTimeout = setTimeout(() => {
          connect();
        }, delay);
      } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        const errorMsg = "Failed to reconnect after multiple attempts";
        Logger.error("[katanaWS] Max reconnection attempts reached, giving up");
        connectionError = errorMsg;
        notifyStatusListeners();
        subscribers.forEach((entry) => entry.onError?.(errorMsg));
      }
    };

    ws = socket;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Unknown error";
    Logger.error("[katanaWS] Failed to create WebSocket connection:", errorMessage, err);
    isConnecting = false;
    const errorMsg = "Failed to create WebSocket connection";
    connectionError = errorMsg;
    notifyStatusListeners();
    subscribers.forEach((entry) => entry.onError?.(errorMsg));
  }
}

/** Send subscribe on the 0 → 1 transition; otherwise just bump the refcount. */
function refSubscription(sub: KatanaSubscription) {
  const key = subKey(sub);
  const existing = subscriptions.get(key);

  if (existing) {
    if (existing.pendingUnsubscribe) {
      clearTimeout(existing.pendingUnsubscribe);
      existing.pendingUnsubscribe = undefined;
    }
    existing.refCount++;
    return;
  }

  subscriptions.set(key, { subscription: sub, refCount: 1 });
  sendWsSubscribe(sub);
}

/** Defer unsubscribe to next microtask so Strict Mode re-mounts collapse to a no-op. */
function unrefSubscription(sub: KatanaSubscription) {
  const key = subKey(sub);
  const entry = subscriptions.get(key);
  if (!entry) return;

  entry.refCount--;
  if (entry.refCount > 0) return;

  entry.pendingUnsubscribe = setTimeout(() => {
    entry.pendingUnsubscribe = undefined;
    if (entry.refCount <= 0) {
      sendWsUnsubscribe(entry.subscription);
      subscriptions.delete(key);
    }
  }, 0);
}

function refSubscriber(id: string, subscriber: Omit<SubscriberEntry, "refCount" | "cleanup">) {
  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  const existing = subscribers.get(id);

  if (existing) {
    if (existing.pendingRemove) {
      clearTimeout(existing.pendingRemove);
      existing.pendingRemove = undefined;
    }
    existing.refCount++;
    return;
  }

  const entry: SubscriberEntry = {
    setup: subscriber.setup,
    onMessage: subscriber.onMessage,
    onError: subscriber.onError,
    cleanup: null,
    refCount: 1,
  };
  subscribers.set(id, entry);

  if (isConnected && ws) {
    runSetup(entry);
  } else if (subscribers.size === 1) {
    connect();
  }
}

function unrefSubscriber(id: string) {
  const entry = subscribers.get(id);
  if (!entry) return;

  entry.refCount--;
  if (entry.refCount > 0) return;

  entry.pendingRemove = setTimeout(() => {
    entry.pendingRemove = undefined;
    if (entry.refCount > 0) return;

    entry.cleanup?.();
    entry.cleanup = null;
    subscribers.delete(id);

    if (subscribers.size === 0) {
      if (disconnectTimeout) clearTimeout(disconnectTimeout);
      disconnectTimeout = setTimeout(() => {
        disconnectTimeout = undefined;
        if (subscribers.size === 0) disconnect();
      }, 0);
    }
  }, 0);
}

function getSnapshot() {
  return { isConnected, error: connectionError };
}

let cachedSnapshot = getSnapshot();

function getSnapshotStable() {
  const next = getSnapshot();
  if (next.isConnected !== cachedSnapshot.isConnected || next.error !== cachedSnapshot.error) {
    cachedSnapshot = next;
  }
  return cachedSnapshot;
}

function subscribeToStatus(listener: StatusListener) {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

let visibilityHandlerRegistered = false;

function registerVisibilityHandler() {
  if (visibilityHandlerRegistered || typeof window === "undefined") return;
  visibilityHandlerRegistered = true;

  window.addEventListener("visibilitychange", () => {
    if (subscribers.size === 0) return;

    if (document.visibilityState === "hidden") {
      disconnect();
    } else {
      connect();
    }
  });
}

// --- React hook wrapping the singleton ---

export interface KatanaWsSubscriber {
  /** Called when the WS is open (and on reconnect). Should return a cleanup/unsubscribe fn. */
  setup: SetupFn;
  /** Called for every incoming WS message. */
  onMessage?: MessageListener;
  onError?: ErrorListener;
}

export interface KatanaWsClient {
  /** Imperative subscribe — multiplexes onto the same shared connection. Returns a tear-down fn. */
  subscribe(subscriber: KatanaWsSubscriber): () => void;
}

const sharedClient: KatanaWsClient = {
  subscribe(subscriber) {
    registerVisibilityHandler();
    const id = `imperative-${imperativeIdCounter++}`;
    refSubscriber(id, {
      setup: subscriber.setup,
      onMessage: subscriber.onMessage,
      onError: subscriber.onError,
    });
    // Imperative callers don't go through React's deferred-cleanup lifecycle —
    // tear down synchronously so the caller's expectations match.
    return () => {
      const entry = subscribers.get(id);
      if (!entry) return;
      entry.cleanup?.();
      entry.cleanup = null;
      subscribers.delete(id);
      if (subscribers.size === 0) {
        if (disconnectTimeout) clearTimeout(disconnectTimeout);
        disconnectTimeout = setTimeout(() => {
          disconnectTimeout = undefined;
          if (subscribers.size === 0) disconnect();
        }, 0);
      }
    };
  },
};

interface UseKatanaWsConnectionProps {
  /**
   * Unique identifier for this subscriber. Multiple hook instances with the
   * same `id` share a single entry, ref-counted across mounts.
   * Required when using `subscription` or `setup`; ignored otherwise.
   */
  id?: string;
  /**
   * Declarative wire subscription. The hook sends the subscribe message on
   * mount (or when the WS opens) and the unsubscribe message on unmount.
   * Reference-counted by descriptor — multiple hooks subscribing to the same
   * descriptor share a single wire subscription.
   */
  subscription?: KatanaSubscription;
  /**
   * Imperative setup callback. Called when the WS is open (or reconnects);
   * should return a cleanup function. Use only when the declarative
   * `subscription` shape doesn't fit (e.g. multi-message handshakes).
   */
  setup?: SetupFn;
  /** Called for every incoming WS message. */
  onMessage?: MessageListener;
  enabled?: boolean;
  onError?: ErrorListener;
}

interface UseKatanaWsConnectionResult {
  isConnected: boolean;
  error: string | null;
  /** Imperative client for callers (e.g. the TradingView datafeed) that subscribe outside of React. */
  client: KatanaWsClient;
}

/**
 * Single source of truth for Katana perps WebSocket access.
 *
 * All hook instances share a single module-level WebSocket connection.
 * Subscribers are deduplicated by `id`, and wire subscriptions are
 * deduplicated by descriptor — so two components asking for the same
 * `l2orderbook` market send only one subscribe message to the server.
 *
 * Three usage modes (combine freely):
 * 1. **Declarative subscription** — pass `id` + `subscription` + `onMessage`.
 *    The hook handles the wire subscribe/unsubscribe lifecycle, ref-counted.
 * 2. **Imperative setup** — pass `id` + `setup`. The hook calls `setup(ws)`
 *    on each (re)connect; return a cleanup to be invoked on unmount.
 * 3. **Client-only** — call without args and use `client.subscribe(...)` for
 *    non-React callers (e.g. TradingView datafeeds).
 */
export const useKatanaWsConnection = ({
  id,
  subscription,
  setup,
  onMessage,
  enabled = true,
  onError,
}: UseKatanaWsConnectionProps = {}): UseKatanaWsConnectionResult => {
  const { isConnected: connected, error } = useSyncExternalStore(subscribeToStatus, getSnapshotStable);

  // Declarative wire subscription — ref-counted by descriptor
  useEffect(() => {
    if (!enabled || !subscription) return;

    registerVisibilityHandler();
    refSubscription(subscription);

    return () => {
      unrefSubscription(subscription);
    };
    // subscription is an object literal — stringify for stable deps
  }, [enabled, subscription ? subKey(subscription) : undefined]);

  // Subscriber registration — ref-counted by id. Skips when there's nothing
  // to register (no setup, no subscription, or no id).
  useEffect(() => {
    if (!enabled || !id || (!setup && !onMessage && !subscription)) return;

    registerVisibilityHandler();
    refSubscriber(id, { setup, onMessage, onError });

    return () => {
      unrefSubscriber(id);
    };
    // Callbacks are expected to be stable (useCallback) at call sites —
    // we only re-register when `id` or `enabled` change.
  }, [enabled, id]);

  return {
    isConnected: enabled && connected,
    error: enabled ? error : null,
    client: sharedClient,
  };
};
